import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const CredentialForm = ({ selectedTemplate, formData, onFormDataChange, onNext, onBack }) => {
  const [validationErrors, setValidationErrors] = useState({});
  const [isValidating, setIsValidating] = useState(false);

  const formatOptions = [
    { value: 'JSON-LD', label: 'JSON-LD (Linked Data)' },
    { value: 'JWT', label: 'JWT (JSON Web Token)' },
    { value: 'VC-JWT', label: 'VC-JWT (Verifiable Credential JWT)' }
  ];

  const expirationOptions = [
    { value: 'never', label: 'Never expires' },
    { value: '1year', label: '1 Year' },
    { value: '2years', label: '2 Years' },
    { value: '5years', label: '5 Years' },
    { value: 'custom', label: 'Custom date' }
  ];

  const revocationPolicyOptions = [
    { value: 'issuer_only', label: 'Issuer Only' },
    { value: 'subject_consent', label: 'Subject Consent Required' },
    { value: 'automatic', label: 'Automatic (on expiry)' },
    { value: 'none', label: 'No Revocation' }
  ];

  const handleInputChange = (field, value) => {
    const updatedData = { ...formData, [field]: value };
    onFormDataChange(updatedData);
    
    // Clear validation error for this field
    if (validationErrors?.[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    setIsValidating(true);
    const errors = {};

    // Validate required fields
    const requiredFields = ['recipient_name', 'recipient_email', 'format', 'expiration_policy'];
    requiredFields?.forEach(field => {
      if (!formData?.[field] || formData?.[field]?.toString()?.trim() === '') {
        errors[field] = 'This field is required';
      }
    });

    // Validate email format
    if (formData?.recipient_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.recipient_email)) {
      errors.recipient_email = 'Please enter a valid email address';
    }

    // Validate custom expiration date
    if (formData?.expiration_policy === 'custom' && !formData?.custom_expiry_date) {
      errors.custom_expiry_date = 'Please select an expiration date';
    }

    // Validate template-specific fields
    selectedTemplate?.fields?.forEach(field => {
      if (!formData?.[field] || formData?.[field]?.toString()?.trim() === '') {
        errors[field] = `${field?.replace('_', ' ')} is required`;
      }
    });

    setValidationErrors(errors);
    setIsValidating(false);
    return Object.keys(errors)?.length === 0;
  };

  const handleNext = () => {
    if (validateForm()) {
      onNext();
    }
  };

  const renderTemplateFields = () => {
    return selectedTemplate?.fields?.map(field => {
      const fieldName = field?.replace('_', ' ')?.replace(/\b\w/g, l => l?.toUpperCase());
      
      if (field?.includes('date')) {
        return (
          <Input
            key={field}
            label={fieldName}
            type="date"
            value={formData?.[field] || ''}
            onChange={(e) => handleInputChange(field, e?.target?.value)}
            error={validationErrors?.[field]}
            required
            className="mb-4"
          />
        );
      }

      if (field === 'gpa') {
        return (
          <Input
            key={field}
            label={fieldName}
            type="number"
            min="0"
            max="4"
            step="0.01"
            placeholder="e.g., 3.75"
            value={formData?.[field] || ''}
            onChange={(e) => handleInputChange(field, e?.target?.value)}
            error={validationErrors?.[field]}
            required
            className="mb-4"
          />
        );
      }

      if (field === 'salary_range') {
        const salaryOptions = [
          { value: '30000-50000', label: '$30,000 - $50,000' },
          { value: '50000-75000', label: '$50,000 - $75,000' },
          { value: '75000-100000', label: '$75,000 - $100,000' },
          { value: '100000-150000', label: '$100,000 - $150,000' },
          { value: '150000+', label: '$150,000+' }
        ];

        return (
          <Select
            key={field}
            label={fieldName}
            options={salaryOptions}
            value={formData?.[field] || ''}
            onChange={(value) => handleInputChange(field, value)}
            error={validationErrors?.[field]}
            placeholder="Select salary range"
            className="mb-4"
          />
        );
      }

      return (
        <Input
          key={field}
          label={fieldName}
          type="text"
          value={formData?.[field] || ''}
          onChange={(e) => handleInputChange(field, e?.target?.value)}
          error={validationErrors?.[field]}
          required
          className="mb-4"
        />
      );
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <Icon name="ArrowLeft" size={20} />
        </Button>
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Configure Credential</h2>
          <p className="text-muted-foreground mt-1">Fill in the details for {selectedTemplate?.name}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Form Section */}
        <div className="space-y-6">
          {/* Recipient Information */}
          <div className="credential-card">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
              <Icon name="User" size={20} className="mr-2" />
              Recipient Information
            </h3>
            <div className="space-y-4">
              <Input
                label="Recipient Name"
                type="text"
                placeholder="Enter full name"
                value={formData?.recipient_name || ''}
                onChange={(e) => handleInputChange('recipient_name', e?.target?.value)}
                error={validationErrors?.recipient_name}
                required
              />
              <Input
                label="Recipient Email"
                type="email"
                placeholder="Enter email address"
                value={formData?.recipient_email || ''}
                onChange={(e) => handleInputChange('recipient_email', e?.target?.value)}
                error={validationErrors?.recipient_email}
                required
              />
              <Input
                label="Recipient ID (Optional)"
                type="text"
                placeholder="Student ID, Employee ID, etc."
                value={formData?.recipient_id || ''}
                onChange={(e) => handleInputChange('recipient_id', e?.target?.value)}
              />
            </div>
          </div>

          {/* Template-Specific Fields */}
          <div className="credential-card">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
              <Icon name="FileText" size={20} className="mr-2" />
              Credential Details
            </h3>
            <div className="space-y-4">
              {renderTemplateFields()}
            </div>
          </div>

          {/* Metadata Configuration */}
          <div className="credential-card">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
              <Icon name="Settings" size={20} className="mr-2" />
              Credential Settings
            </h3>
            <div className="space-y-4">
              <Select
                label="Credential Format"
                options={formatOptions}
                value={formData?.format || selectedTemplate?.format}
                onChange={(value) => handleInputChange('format', value)}
                error={validationErrors?.format}
                placeholder="Select format"
              />
              
              <Select
                label="Expiration Policy"
                options={expirationOptions}
                value={formData?.expiration_policy || 'never'}
                onChange={(value) => handleInputChange('expiration_policy', value)}
                error={validationErrors?.expiration_policy}
                placeholder="Select expiration"
              />

              {formData?.expiration_policy === 'custom' && (
                <Input
                  label="Custom Expiry Date"
                  type="date"
                  value={formData?.custom_expiry_date || ''}
                  onChange={(e) => handleInputChange('custom_expiry_date', e?.target?.value)}
                  error={validationErrors?.custom_expiry_date}
                  required
                />
              )}

              <Select
                label="Revocation Policy"
                options={revocationPolicyOptions}
                value={formData?.revocation_policy || 'issuer_only'}
                onChange={(value) => handleInputChange('revocation_policy', value)}
                placeholder="Select revocation policy"
              />

              <Input
                label="Issuer Notes (Optional)"
                type="text"
                placeholder="Internal notes about this credential"
                value={formData?.issuer_notes || ''}
                onChange={(e) => handleInputChange('issuer_notes', e?.target?.value)}
              />
            </div>
          </div>

          {/* Privacy & Security Settings */}
          <div className="credential-card">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
              <Icon name="Shield" size={20} className="mr-2" />
              Privacy & Security
            </h3>
            <div className="space-y-4">
              <Checkbox
                label="Enable Zero-Knowledge Proofs"
                description="Allow selective disclosure of credential attributes"
                checked={formData?.enable_zkp || false}
                onChange={(e) => handleInputChange('enable_zkp', e?.target?.checked)}
              />
              
              <Checkbox
                label="Blockchain Anchoring"
                description="Anchor credential hash on blockchain for immutable verification"
                checked={formData?.blockchain_anchor || true}
                onChange={(e) => handleInputChange('blockchain_anchor', e?.target?.checked)}
              />
              
              <Checkbox
                label="Allow Credential Sharing"
                description="Recipient can share this credential with third parties"
                checked={formData?.allow_sharing || true}
                onChange={(e) => handleInputChange('allow_sharing', e?.target?.checked)}
              />
              
              <Checkbox
                label="Require Biometric Verification"
                description="Add biometric verification requirement for high-security credentials"
                checked={formData?.require_biometric || false}
                onChange={(e) => handleInputChange('require_biometric', e?.target?.checked)}
              />
            </div>
          </div>
        </div>

        {/* Preview Section */}
        <div className="space-y-6">
          <div className="credential-card sticky top-6">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
              <Icon name="Eye" size={20} className="mr-2" />
              Credential Preview
            </h3>
            
            {/* Preview Card */}
            <div className="border-2 border-dashed border-border rounded-lg p-6 bg-muted/30">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-foreground">{selectedTemplate?.name}</h4>
                  <span className="px-2 py-1 bg-primary text-primary-foreground text-xs rounded-full">
                    {formData?.format || selectedTemplate?.format}
                  </span>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Recipient:</span>
                    <span className="text-foreground">{formData?.recipient_name || 'Not specified'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Email:</span>
                    <span className="text-foreground">{formData?.recipient_email || 'Not specified'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Expiration:</span>
                    <span className="text-foreground">
                      {formData?.expiration_policy === 'never' ? 'Never expires' : 
                       formData?.expiration_policy === 'custom' ? formData?.custom_expiry_date || 'Not set' :
                       formData?.expiration_policy?.replace(/(\d+)/, '$1 ')?.replace('years', 'Years') || 'Not set'}
                    </span>
                  </div>
                </div>

                <div className="border-t border-border pt-4">
                  <h5 className="text-sm font-medium text-foreground mb-2">Credential Fields:</h5>
                  <div className="space-y-1">
                    {selectedTemplate?.fields?.map(field => (
                      <div key={field} className="flex justify-between text-xs">
                        <span className="text-muted-foreground capitalize">{field?.replace('_', ' ')}:</span>
                        <span className="text-foreground">{formData?.[field] || 'Not filled'}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border-t border-border pt-4">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Icon name="Shield" size={12} />
                    <span>Cryptographically signed</span>
                  </div>
                  {formData?.blockchain_anchor && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                      <Icon name="Link" size={12} />
                      <span>Blockchain anchored</span>
                    </div>
                  )}
                  {formData?.enable_zkp && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                      <Icon name="Eye" size={12} />
                      <span>Zero-knowledge enabled</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Security Indicators */}
            <div className="mt-4 space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Icon name="CheckCircle" size={16} className="text-success" />
                <span className="text-muted-foreground">Digital signature ready</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Icon name="Key" size={16} className="text-success" />
                <span className="text-muted-foreground">Cryptographic keys generated</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Icon name="Database" size={16} className="text-success" />
                <span className="text-muted-foreground">Blockchain connection active</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Action Buttons */}
      <div className="flex justify-between pt-6 border-t border-border">
        <Button variant="outline" onClick={onBack}>
          Back to Templates
        </Button>
        <Button 
          onClick={handleNext}
          loading={isValidating}
          iconName="ArrowRight" 
          iconPosition="right"
        >
          Review & Issue
        </Button>
      </div>
    </div>
  );
};

export default CredentialForm;